import { differenceInDays, isSameDay } from "date-fns";

import type { ExtendedEventInput } from "../controllers/events";

const aggregatedAllDayEvent = "aggregatedAllDayEventId:";

export const isAggregatedAllDayEvent = (eventId: string | undefined): boolean =>
  !!eventId?.includes(aggregatedAllDayEvent);

export const generateAggregatedAllDayEvents = (
  dates: Date[],
  events: ExtendedEventInput[],
  defaultCalendarId: string
): {
  isSingleRowOfAllDayEvents: boolean;
  aggregatedAllDayEvents: ExtendedEventInput[];
} => {
  const aggregatedAllDayEvents: ExtendedEventInput[] = [];
  // Number of allDay events per day
  const counters: number[] = [0, 0, 0, 0, 0, 0, 0];
  // CalendarId per day
  const calendarIds: string[] = [];
  events.map(async ({ end, start, extendedProps: { calendarId } }) => {
    if (!(end instanceof Date) || !(start instanceof Date)) return;
    const firstDayOfTheWeek = dates[0];
    const duration = differenceInDays(end, start);
    const diffWithStartOfWeek = differenceInDays(start, firstDayOfTheWeek);
    const isEventStartedLastWeek = diffWithStartOfWeek < 0;
    const limit = isEventStartedLastWeek
      ? // When the event started before the current week
        duration + diffWithStartOfWeek
      : duration;
    for (let i = 0; i < limit; i++) {
      const dayIndex = isEventStartedLastWeek
        ? i
        : // When the event started during the current week
          i + dates.findIndex((date) => isSameDay(start, date));
      if (dayIndex < dates.length) {
        counters[dayIndex]++;
        // set counter event's color using calendarId
        calendarIds[dayIndex] = calendarId || defaultCalendarId;
      }
    }
  });
  for (const [dayIndex, date] of dates.entries()) {
    const calendarId = calendarIds[dayIndex];
    const newEvents: ExtendedEventInput[] = [];
    const counter = counters[dayIndex];
    if (counter > 0)
      newEvents.push(
        buildAggregatedAllDayEvent(date, counter, calendarId, dayIndex)
      );
    newEvents.forEach((e) => aggregatedAllDayEvents.push(e));
  }
  const isSingleRowOfAllDayEvents = !counters.some((counter) => counter > 1);
  return { isSingleRowOfAllDayEvents, aggregatedAllDayEvents };
};

const buildAggregatedAllDayEvent = (
  start: Date,
  counter: number,
  calendarId: string,
  dayIndex: number
): ExtendedEventInput => {
  const id = aggregatedAllDayEvent + dayIndex;
  const allDay = true;
  const title = `${counter} ${counter === 1 ? "event" : "events"}`;
  return {
    id,
    title,
    start,
    allDay,
    extendedProps: {
      calendarId,
      overlappingIndex: 0,
    },
  };
};
